import React, { useEffect, useState } from "react";
import {
  MainContainer,
  ChatContainer,
  MessageList,
  Message,
  MessageInput,
} from "@chatscope/chat-ui-kit-react";
import "@chatscope/chat-ui-kit-styles/dist/default/styles.min.css";
import { IconButton } from "@mui/material";
import RobotIcon from "mdi-react/RobotIcon";
import { io } from 'socket.io-client';
import { API_ENDPOINT, API_SIGNATURE_HEADER } from "settings";
import userService from "services/user";
import IUser from "interfaces/models/core/user/user";
import { AI_SENDER, CONNECT_EVENT, FROM_AI_EVENT, GATEWAY_PATH, INCOMING, JOIN_ROOM_EVENT, OUTGOING, styles, TO_AI_EVENT, YOU_SENDER } from "./settings";
import { useTranslation } from "react-i18next";
import tokenService from "services/token";
//import { marked } from "marked";
//import DOMPurify from "dompurify";

const socket = io(API_ENDPOINT, {
  path: API_ENDPOINT.includes('localhost') ? '' : GATEWAY_PATH,
  autoConnect: false,
  withCredentials: true,
  extraHeaders: {
    'X-Signature': API_SIGNATURE_HEADER
  }
});

const ChatWidget: React.FC = () => {
  const { t } = useTranslation();
  
  const [isOpen, setIsOpen] = useState<boolean>(false); 
  const [messages, setMessages] = useState([]);
  const [room, setRoom] = useState<string>(""); 
  const [currentUser, setCurrentUser] = useState<IUser>();

  useEffect(() => {
    if (isOpen) {
        tokenService.getToken().subscribe((token) => {
          userService.current().subscribe((data) => {
            setCurrentUser(data);
            socket.auth = { user: data.id, token: token };
            setRoom(`personal-room-${data.id}`);
            socket.connect();
          });
        });
    } else {
      socket.disconnect();
      setMessages([]);
    }
  
    return () => {
      socket.disconnect();
    };
  }, [isOpen]);

  useEffect(() => {
    socket.on(CONNECT_EVENT, () => {
      const initialMessageText = t("APP_CHAT_INITIAL_MESSAGE");

      if (currentUser) {
        socket.emit(JOIN_ROOM_EVENT, { room });

        setMessages((prevMessages) => {
          const lastMessage = prevMessages[prevMessages.length - 1];
    
          const isLastMessageInitial =
            lastMessage?.message === initialMessageText &&
            lastMessage?.sender === AI_SENDER;
    
          if (!isLastMessageInitial) {
            socket.emit(FROM_AI_EVENT, {
              room,
              message: initialMessageText,
              user: currentUser.id,
            });
    
            return [
              ...prevMessages,
              {
                message: initialMessageText,
                sender: AI_SENDER,
                direction: INCOMING,
                sentTime: new Date().toLocaleTimeString(),
              },
            ];
          }
    
          return prevMessages;
        });
      } 
    });

    socket.on(FROM_AI_EVENT, (data: any) => {
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          message: data?.message,
          sender: AI_SENDER,
          direction: INCOMING,
          sentTime: new Date().toLocaleTimeString(),
        },
      ]);
    });

    return () => {
      socket.off(CONNECT_EVENT);
      socket.off(FROM_AI_EVENT);
    };
  }, [room]);

  const handleSendMessage = (text: string) => {
    if (text.trim() !== "" && currentUser) {
      socket.emit(TO_AI_EVENT, { room, message: text, fullContext: messages });
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          message: `👤 ${text}`,
          sender: YOU_SENDER,
          direction: OUTGOING,
          sentTime: new Date().toLocaleTimeString(),
        },
      ]);
    }
  };

  const messageTreatment = (message: string) => {
      return message.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  }

  return (
    <div style={styles.widgetContainer}>
      {isOpen ? (
        <div style={styles.chatBox}>
          <div style={styles.header}>
            <span>Chip</span>
            <button
              style={styles.closeButton}
              onClick={() => setIsOpen(false)}
              aria-label={t("APP_CHAT_MINIMIZE")}
            >
              _
            </button>
          </div>
          <MainContainer>
            <ChatContainer>
              <MessageList>
              {messages.map((message, index) => (
                 <Message
                  key={index}
                  model={{
                    sentTime: message.sentTime,
                    sender: message.sender,
                    direction: message.direction,
                    position: message.position,
                  }}
                >
                  <Message.TextContent>
                    <span dangerouslySetInnerHTML={{ __html: messageTreatment(message.message) }} />
                  </Message.TextContent>
                  <Message.Footer>{message.sentTime}</Message.Footer>
                </Message>
              ))}
              </MessageList>
              <MessageInput attachButton={false} placeholder={t("APP_CHAT_TYPE_MESSAGE")} onSend={handleSendMessage}/>
            </ChatContainer>
          </MainContainer>
        </div>
      ) : (
        <IconButton
            style={styles.openButton}
            onClick={() => setIsOpen(true)}
            size="large">
            <RobotIcon />
        </IconButton>
      )}
    </div>
  );
};

export default ChatWidget;
